/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BillingAccountBulkCreate } from '../models/BillingAccountBulkCreate';
import type { BillingAccountCreate } from '../models/BillingAccountCreate';
import type { BillingAccountDetails } from '../models/BillingAccountDetails';
import type { BillingAccountUpdate } from '../models/BillingAccountUpdate';
import type { BillingAccountUserAddDTO } from '../models/BillingAccountUserAddDTO';
import type { BillingAccountUserCreate } from '../models/BillingAccountUserCreate';
import type { DistrictPaymentConfigDetails } from '../models/DistrictPaymentConfigDetails';
import type { EnrollmentDetails } from '../models/EnrollmentDetails';
import type { EnrollmentUpdate } from '../models/EnrollmentUpdate';
import type { IsOnAutopay } from '../models/IsOnAutopay';
import type { IVRBillingAccount } from '../models/IVRBillingAccount';
import type { IVRQuery } from '../models/IVRQuery';
import type { PagedItems } from '../models/PagedItems';
import type { PagingQuery } from '../models/PagingQuery';
import type { UserDetails } from '../models/UserDetails';
import { request as __request } from '../core/request';

export class BillingAccountsVService {

    /**
     * @param requestBody
     * @returns PagedItems OK
     * @throws ApiError
     */
    public static async billingAccountsVGetAll(
        requestBody?: PagingQuery,
    ): Promise<PagedItems> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/get`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetExport(
        requestBody?: PagingQuery,
    ): Promise<BillingAccountDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/export`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVUpdateBillingAccount(
        billingAccountId: string,
        requestBody?: BillingAccountUpdate,
    ): Promise<BillingAccountDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetById(
        billingAccountId: string,
    ): Promise<BillingAccountDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param userId
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetByUserId(
        userId?: string,
    ): Promise<Array<BillingAccountDetails>> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts`,
            query: {
                'userId': userId,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns BillingAccountDetails Created
     * @throws ApiError
     */
    public static async billingAccountsVCreate(
        requestBody?: BillingAccountCreate,
    ): Promise<BillingAccountDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param userId
     * @param accountNumber
     * @param districtId
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetByAccount(
        userId?: string,
        accountNumber?: string,
        districtId?: string,
    ): Promise<BillingAccountDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/account`,
            query: {
                'userId': userId,
                'accountNumber': accountNumber,
                'districtId': districtId,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns EnrollmentDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetEnrollment(
        billingAccountId: string,
    ): Promise<EnrollmentDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/enrollment`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns EnrollmentDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVUpdateEnrollment(
        billingAccountId: string,
        requestBody?: EnrollmentUpdate,
    ): Promise<EnrollmentDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/enrollment`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns UserDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVCreateUser(
        billingAccountId: string,
        requestBody?: BillingAccountUserCreate,
    ): Promise<UserDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/create_user`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns UserDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVAddUser(
        billingAccountId: string,
        requestBody?: BillingAccountUserAddDTO,
    ): Promise<UserDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/add_user`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param userId
     * @returns boolean OK
     * @throws ApiError
     */
    public static async billingAccountsVVerifyIfIsBillingAccount(
        billingAccountId: string,
        userId?: string,
    ): Promise<boolean> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/verify`,
            query: {
                'userId': userId,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns IVRBillingAccount OK
     * @throws ApiError
     */
    public static async billingAccountsVGetIvr(
        requestBody?: IVRQuery,
    ): Promise<IVRBillingAccount> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/ivr`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns BillingAccountDetails Created
     * @throws ApiError
     */
    public static async billingAccountsVBulkCreate(
        requestBody?: Array<BillingAccountBulkCreate>,
    ): Promise<Array<BillingAccountDetails>> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/bulk`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param districtId
     * @param requestBody
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetAllBulk(
        districtId: string,
        requestBody?: Array<string>,
    ): Promise<Array<BillingAccountDetails>> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/bulk/district/${districtId}`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any No Content
     * @throws ApiError
     */
    public static async billingAccountsVDeactivateAll(
        requestBody?: Array<string>,
    ): Promise<any> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/deactivate`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param districtId
     * @param requestBody
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVUpdateTokens(
        districtId?: string,
        requestBody?: DistrictPaymentConfigDetails,
    ): Promise<Array<BillingAccountDetails>> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/tokens`,
            query: {
                'districtId': districtId,
            },
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns IsOnAutopay OK
     * @throws ApiError
     */
    public static async billingAccountsVGetIsOnAutopay(
        billingAccountId: string,
    ): Promise<IsOnAutopay> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/is-on-autopay`,
        });
        return result.body;
    }

    /**
     * @param districtId
     * @param requestBody
     * @returns BillingAccountDetails OK
     * @throws ApiError
     */
    public static async billingAccountsVGetAllByRelAccount(
        districtId?: string,
        requestBody?: Array<number>,
    ): Promise<Array<BillingAccountDetails>> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/rel-accounts`,
            query: {
                'districtId': districtId,
            },
            body: requestBody,
        });
        return result.body;
    }

}